import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Button,
    Alert,
    TextField,
    FormControlLabel,
    Switch,
    Snackbar,
    Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import ImageUploader from '../../components/ImageUploader';
import {ProtectedFieldSet, useAuthDisabled} from "../../context/AuthDisabledContext";
import Protected from "../../components/Protected";
import {ROLES} from "../../config/roles"; // Make sure ImageUploader is in a shared components folder


const CategoryEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const requiredAction = isNew ? 'create' : 'update';

    // Form state for a Category
    const [name, setName] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [price, setPrice] = useState(0);
    const [active, setActive] = useState(true);

    // Page state
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            setLoading(true);
            apiService.get(`/api/categories/${id}`)
                .then(data => {
                    setName(data.name || '');
                    setIdentifier(data.identifier || '');
                    setDescription(data.description || '');
                    setImageUrl(data.imageUrl || '');
                    setPrice(data.price || 0);
                    setActive(data.active);
                })
                .catch(err => setError(err.message || 'Failed to load category data'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);

        const categoryData = {
            id: isNew ? null : id,
            name, identifier, description, imageUrl, price, active
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/categories', categoryData)
                : apiService.put(`/api/categories/${id}`, categoryData);

            await apiCall;
            setSnackbar({open: true, message: 'Category saved successfully!'});
            setTimeout(() => navigate('/categories'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save category');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error && !isNew) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Category' : `Edit Category: ${name}`}
            </Typography>

            <Protected component={CategoryEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        {/* Left Column: Main Details */}
                        <Grid size={{xs: 12, md: 6}}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Configuration</Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="Category Name"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        label="Identifier"
                                        value={identifier}
                                        onChange={e => setIdentifier(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Description"
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        type="number"
                                        label="Price"
                                        value={price}
                                        onChange={e => setPrice(parseFloat(e.target.value))}
                                        inputProps={{step: "1"}}
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={active} onChange={e => setActive(e.target.checked)}/>}
                                        label="Active"
                                    />
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* Right Column: Image Uploader */}
                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader
                                imageUrl={imageUrl}
                                onImageUrlChange={setImageUrl}
                                uploadPath="MachineCategory"
                                baseFileName={name}
                                isUploadEnabled={!!name}
                            />
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={CategoryEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Category'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/categories" variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

CategoryEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default CategoryEditPage;